import type { Banner } from '@/models/types';
import type { AlertProps } from '@mantine/core';
import DOMPurify from 'dompurify';
import isObject from 'lodash/isObject';
import { z } from 'zod';

const sanitizeText = (value: string): string =>
  DOMPurify.sanitize(value, { ALLOWED_ATTR: [], ALLOWED_TAGS: [] }).trim();

const sanitizedString = z.string().transform(sanitizeText).pipe(z.string().min(1));

const alertParamsSchema = z
  .object({
    autoContrast: z.boolean(),
    color: sanitizedString,
    radius: z.union([z.number().nonnegative().finite(), sanitizedString]),
    title: sanitizedString,
    variant: sanitizedString,
  })
  .partial()
  .transform(data => {
    const { autoContrast, color, radius, title, variant } = data;
    return {
      autoContrast,
      color,
      radius,
      title,
      variant,
    };
  });

type AllowedAlertParams = Pick<
  AlertProps,
  'autoContrast' | 'color' | 'radius' | 'title' | 'variant'
>;

export const getSafeBannerString = (value: unknown): string | undefined => {
  const result = sanitizedString.safeParse(value);
  return result.success ? result.data : undefined;
};

export const getSafeAlertParams = (params: Banner['params']): AllowedAlertParams => {
  if (!isObject(params) || Array.isArray(params)) {
    return {};
  }

  const result = alertParamsSchema.safeParse(params);
  return result.success ? (result.data as AllowedAlertParams) : {};
};
